/**
 * Utility functions for rubric workflow question processing
 */

const getAssignedWorkflowId = (
  version: CQA.RubricVersion | undefined,
  workflowType: CQA.CodeWorkflowType,
): number | null => {
  if (!version) {
    return null;
  }

  if (workflowType === 'SPAN' && version.spanWorkflowId != null) {
    return version.spanWorkflowId;
  }

  const workflowIds = version.codeWorkflows?.[workflowType];
  return workflowIds?.[0] ?? null;
};

/**
 * Gets the workflow for a specific code type from the rubric version
 *
 * @param rubricVersion - Full rubric version with all workflows
 * @param codeType - Type of code ('procedure', 'diagnosis', 'modifier')
 * @returns The code workflow or undefined if not found
 */
export const getWorkflowForCodeType = (
  rubricVersion: CQA.FullRubricVersion | null | undefined,
  codeType: string
): CQA.CodeWorkflow | undefined => {
  if (!rubricVersion?.workflows) {
    return undefined;
  }

  // Map code type to workflow type
  const workflowTypeMap: Record<string, CQA.CodeWorkflowType> = {
    'procedure': 'PROCEDURE',
    'diagnosis': 'DIAGNOSIS',
    'modifier': 'MODIFIER',
    'case': 'CASE',
    'span': 'SPAN'
  };

  const workflowType = workflowTypeMap[codeType.toLowerCase()];
  if (!workflowType) {
    return undefined;
  }

  if (workflowType === 'SPAN') {
    const spanWorkflowId = getAssignedWorkflowId(rubricVersion.version, workflowType);
    const workflowEntry = rubricVersion.workflows.find(
      w => w.workflow.codeType === workflowType && (
        spanWorkflowId == null || w.workflow.id === spanWorkflowId
      ),
    );

    if (!workflowEntry) {
      return undefined;
    }

    return {
      ...workflowEntry.workflow,
      questions: workflowEntry.questions
    };
  }

  const workflowEntry = rubricVersion.workflows.find(
    w => w.workflow.codeType === workflowType,
  );

  if (!workflowEntry) {
    return undefined;
  }

  // Return workflow object with questions attached
  // (questions are a sibling to workflow in the API response structure)
  return {
    ...workflowEntry.workflow,
    questions: workflowEntry.questions
  };
};

/**
 * Finds an attribute for a specific question from the code's attributes
 *
 * @param attributes - Array of attributes
 * @param questionId - The question ID to find
 * @returns The matching attribute or undefined
 */
export const findAnswerForQuestion = (
  attributes: CQA.Attribute[] | undefined,
  questionId: number
): CQA.Attribute | undefined => {
  if (!attributes) {
    return undefined;
  }
  return attributes.find(a => a.questionId === questionId);
};

/**
 * Maps a code type string to the corresponding attribute entity type for API calls.
 * This is a centralized mapping used across all rubric question components.
 *
 * @param codeType - The code type ('procedure', 'diagnosis', 'modifier', 'case')
 * @returns The corresponding attribute entity type
 */
/**
 * Gets question IDs that should be cleared when a question's answer changes.
 * Uses the backend-provided visible questions to determine downstream questions
 * and also finds stale attributes from previously visited workflow branches.
 *
 * @param visibleQuestions - Currently visible workflow questions (from backend evaluation)
 * @param attributes - Current saved attributes on the entity
 * @param changedQuestionId - The question whose answer is changing
 * @returns Array of question IDs whose attributes should be deleted
 */
export const getQuestionIdsToClear = (
  visibleQuestions: CQA.WorkflowQuestion[],
  attributes: CQA.Attribute[],
  changedQuestionId: number
): number[] => {
  // Find downstream: all visible questions after the changed question
  const changedIndex = visibleQuestions.findIndex(
    wq => wq.question.id === changedQuestionId
  );
  const downstream = changedIndex >= 0
    ? visibleQuestions
      .slice(changedIndex + 1)
      .map(wq => wq.question.id)
    : [];

  // Find stale: attributes for questions not currently visible (from old branches)
  const visibleIds = new Set(visibleQuestions.map(wq => wq.question.id));
  visibleIds.add(changedQuestionId);
  const staleIds = attributes
    .filter(a => !visibleIds.has(a.questionId))
    .map(a => a.questionId);

  // Combine and deduplicate
  return [...new Set([...downstream, ...staleIds])];
};

export const codeTypeToEntityType = (codeType: string): CQA.AttributeEntityType => {
  switch (codeType.toLowerCase()) {
    case 'procedure': return 'code_procedure';
    case 'diagnosis': return 'code_diagnosis';
    case 'modifier': return 'code_modifier';
    case 'case': return 'case';
    case 'span': return 'span';
    default: return 'code_procedure'; // fallback
  }
};

const attributeSpanScopeId = (attribute: CQA.Attribute): number | null | undefined =>
  attribute.spanScopeAttributeId ?? attribute.parentQuestionId;

const isUnscopedAttribute = (attribute: CQA.Attribute): boolean =>
  attributeSpanScopeId(attribute) == null;

export interface SpanScopedWorkflowMatch {
  /** The source SPANS question whose saved answer selected the active span. */
  sourceQuestion: CQA.Question;
  /** Workflow that contains the source SPANS question. */
  sourceWorkflow: CQA.CodeWorkflow;
  /** The concrete source SPANS answer row. Used as spanScopeAttributeId for scoped span answers. */
  sourceAttribute: CQA.Attribute;
  /** The collection-level SPAN workflow. */
  spanWorkflow: CQA.CodeWorkflow;
  /** Questions inside the SPAN workflow scoped to sourceQuestion. */
  workflowQuestions: CQA.WorkflowQuestion[];
  /** Owning code when the source answer lives on a code workflow. Undefined for case-level sources. */
  ownerCode?: CQA.ExtractedCode;
  /** Code context for evaluating source-scoped questions against the source answer owner. */
  codeContext?: CQA.WorkflowEvaluationRequest['codeContext'];
}

const codesForWorkflow = (
  attachedCodes: CQA.AttachedCodes | undefined,
  workflow: CQA.CodeWorkflow,
): CQA.ExtractedCode[] => {
  if (!attachedCodes) return [];
  switch (workflow.codeType) {
    case 'PROCEDURE': return attachedCodes.procedures || [];
    case 'DIAGNOSIS': return attachedCodes.diagnoses || [];
    case 'MODIFIER':  return attachedCodes.modifiers || [];
    default: return [];
  }
};

/**
 * Finds question-level source-scoped SPAN workflow sections for the active span.
 *
 * The source of truth is the collection-level SPAN workflow. Questions marked
 * `SELECTED_BY_SPANS_QUESTION` are grouped by `spanScopeQuestionId`, then matched
 * against concrete SPANS answer attributes whose `spanAnnotationIds` include the
 * active span. The source attribute id is the storage discriminator for answers.
 */
export const findSpanScopedWorkflowMatchesForSpan = (
  rubricVersion: CQA.FullRubricVersion | null | undefined,
  spanId: number | string | undefined,
  attachedCodes: CQA.AttachedCodes | undefined,
  caseAttributes: CQA.Attribute[] | undefined,
): SpanScopedWorkflowMatch[] => {
  if (spanId == null) return [];
  const activeSpanId = String(spanId);
  const workflows = rubricVersion?.workflows;
  if (!workflows?.length) return [];

  const spanWorkflowId = getAssignedWorkflowId(rubricVersion?.version, 'SPAN');
  const spanWorkflowEntry = workflows.find(entry =>
    entry.workflow.codeType === 'SPAN' &&
    (spanWorkflowId == null || entry.workflow.id === spanWorkflowId),
  );
  if (!spanWorkflowEntry?.questions?.length) return [];

  const spanWorkflow: CQA.CodeWorkflow = {
    ...spanWorkflowEntry.workflow,
    questions: spanWorkflowEntry.questions,
  };

  const scopedQuestionsBySourceId = new Map<number, CQA.WorkflowQuestion[]>();
  for (const workflowQuestion of spanWorkflowEntry.questions) {
    if (
      workflowQuestion.spanScope === 'SELECTED_BY_SPANS_QUESTION' &&
      workflowQuestion.spanScopeQuestionId != null
    ) {
      const existing = scopedQuestionsBySourceId.get(workflowQuestion.spanScopeQuestionId) || [];
      existing.push(workflowQuestion);
      scopedQuestionsBySourceId.set(workflowQuestion.spanScopeQuestionId, existing);
    }
  }
  if (scopedQuestionsBySourceId.size === 0) return [];

  const sourceQuestionsById = new Map<number, CQA.Question>();
  for (const entry of workflows) {
    for (const workflowQuestion of entry.questions || []) {
      if (scopedQuestionsBySourceId.has(workflowQuestion.question.id)) {
        sourceQuestionsById.set(workflowQuestion.question.id, workflowQuestion.question);
      }
    }
  }

  const matches: SpanScopedWorkflowMatch[] = [];

  const addMatch = (
    sourceWorkflow: CQA.CodeWorkflow,
    sourceAttribute: CQA.Attribute,
    ownerCode?: CQA.ExtractedCode,
  ) => {
    const selectedSpanIds = sourceAttribute.spanAnnotationIds || [];
    if (sourceAttribute.id == null || !selectedSpanIds.some(id => String(id) === activeSpanId)) {
      return;
    }
    const scopedQuestions = scopedQuestionsBySourceId.get(sourceAttribute.questionId);
    const sourceQuestion = sourceQuestionsById.get(sourceAttribute.questionId);
    if (!scopedQuestions?.length || !sourceQuestion) {
      return;
    }
    matches.push({
      sourceQuestion,
      sourceWorkflow,
      sourceAttribute,
      spanWorkflow,
      workflowQuestions: scopedQuestions,
      ownerCode,
      codeContext: ownerCode ? {
        original: ownerCode.original ?? null,
        code: ownerCode.value,
        sequenceNumber: ownerCode.sequenceNumber ?? 0,
        codeType: ownerCode.codeType,
      } : undefined,
    });
  };

  for (const entry of workflows) {
    if (!entry.questions?.some(wq => scopedQuestionsBySourceId.has(wq.question.id))) {
      continue;
    }

    const sourceWorkflow: CQA.CodeWorkflow = {
      ...entry.workflow,
      questions: entry.questions,
    };
    const sourceQuestionIds = new Set(
      entry.questions
        .map(wq => wq.question)
        .filter(question => question.questionType === 'SPANS')
        .map(question => question.id),
    );
    if (sourceQuestionIds.size === 0) {
      continue;
    }

    if (sourceWorkflow.codeType === 'CASE') {
      (caseAttributes || [])
        .filter(attribute => sourceQuestionIds.has(attribute.questionId) && isUnscopedAttribute(attribute))
        .forEach(attribute => addMatch(sourceWorkflow, attribute));
      continue;
    }

    const codes = codesForWorkflow(attachedCodes, sourceWorkflow);
    for (const code of codes) {
      (code.attributes || [])
        .filter(attribute => sourceQuestionIds.has(attribute.questionId) && isUnscopedAttribute(attribute))
        .forEach(attribute => addMatch(sourceWorkflow, attribute, code));
    }
  }

  return matches;
};
